/** Small inline icon set. Decorative only: every button carries a real label. */

const base = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
};

export function IconSelect() {
  return (
    <svg {...base}>
      <path d="M5 3l6.5 16 2.2-6.3L20 10.5z" />
    </svg>
  );
}

export function IconRect() {
  return (
    <svg {...base}>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" />
    </svg>
  );
}

export function IconEllipse() {
  return (
    <svg {...base}>
      <ellipse cx="12" cy="12" rx="8.5" ry="6.5" />
    </svg>
  );
}

export function IconDiamond() {
  return (
    <svg {...base}>
      <path d="M12 3.5L20.5 12 12 20.5 3.5 12z" />
    </svg>
  );
}

export function IconArrow() {
  return (
    <svg {...base}>
      <path d="M4 19L19 5" />
      <path d="M12.5 5H19v6.5" />
    </svg>
  );
}

export function IconLine() {
  return (
    <svg {...base}>
      <path d="M4 19L20 5" />
    </svg>
  );
}

export function IconPencil() {
  return (
    <svg {...base}>
      <path d="M4 20l4-1 10-10-3-3L5 16z" />
      <path d="M14.5 5.5l3 3" />
    </svg>
  );
}

export function IconText() {
  return (
    <svg {...base}>
      <path d="M5 6h14" />
      <path d="M12 6v13" />
    </svg>
  );
}

export function IconEraser() {
  return (
    <svg {...base}>
      <path d="M8 20h11" />
      <path d="M13 4.5l6.5 6.5-7 7H8L4.5 14.5z" />
    </svg>
  );
}

export function IconHand() {
  return (
    <svg {...base}>
      <path d="M8 12V6.5a1.5 1.5 0 013 0V12" />
      <path d="M11 11V5.5a1.5 1.5 0 013 0V12" />
      <path d="M14 11.5v-4a1.5 1.5 0 013 0V15a5 5 0 01-5 5h-1a5 5 0 01-5-5v-3l-1.2.7a1.6 1.6 0 00-.6 2.1" />
    </svg>
  );
}

export function IconPlus() {
  return (
    <svg {...base}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function IconMinus() {
  return (
    <svg {...base}>
      <path d="M5 12h14" />
    </svg>
  );
}

export function IconFit() {
  return (
    <svg {...base}>
      <path d="M4 9V4h5M20 15v5h-5M20 9V4h-5M4 15v5h5" />
    </svg>
  );
}

export function IconClose() {
  return (
    <svg {...base}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function IconSun() {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
    </svg>
  );
}

export function IconMoon() {
  return (
    <svg {...base}>
      <path d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z" />
    </svg>
  );
}

export function IconLock({ open = false }: { readonly open?: boolean }) {
  return (
    <svg {...base}>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      {open ? <path d="M8 11V8a4 4 0 017.5-2" /> : <path d="M8 11V8a4 4 0 018 0v3" />}
    </svg>
  );
}

export function IconDownload() {
  return (
    <svg {...base}>
      <path d="M12 4v10" />
      <path d="M8.5 10.5L12 14l3.5-3.5" />
      <path d="M5 17.5V19a1 1 0 001 1h12a1 1 0 001-1v-1.5" />
    </svg>
  );
}

export function IconUpload() {
  return (
    <svg {...base}>
      <path d="M12 15V5" />
      <path d="M8.5 8.5L12 5l3.5 3.5" />
      <path d="M5 17.5V19a1 1 0 001 1h12a1 1 0 001-1v-1.5" />
    </svg>
  );
}

export function IconList() {
  return (
    <svg {...base}>
      <path d="M9 7h11M9 12h11M9 17h11" />
      <path d="M4.5 7h.01M4.5 12h.01M4.5 17h.01" />
    </svg>
  );
}

export function IconHelp() {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.7 9.4a2.4 2.4 0 014.6.9c0 1.6-2.3 1.9-2.3 3.4" />
      <path d="M12 17.2h.01" />
    </svg>
  );
}

export function IconChevron() {
  return (
    <svg {...base} width={14} height={14}>
      <path d="M7 10l5 5 5-5" />
    </svg>
  );
}

/** Stroke weight preview, so the control shows what it does. */
export function IconWeight({ width }: { readonly width: number }) {
  return (
    <svg {...base} strokeWidth={width}>
      <path d="M4 12h16" />
    </svg>
  );
}

/** Dash pattern preview. */
export function IconDash({ dash }: { readonly dash: string | undefined }) {
  return (
    <svg {...base} strokeWidth={2} strokeDasharray={dash}>
      <path d="M4 12h16" />
    </svg>
  );
}
